import React, { ReactElement, useContext, useMemo } from 'react';
import { BannerCoursesContainerProps } from './bannerCourses.model';
import {
  CustomerBannerContainerStyled,
  CustomerBannerLabelStyled,
} from '../advantagesCustomerBanners.styled';
import {
  BannerCoursesButtonsStyled,
  BannerCoursesHeadStyled,
  BannerCoursesTitleStyled,
  BannerCoursesWrapStyled,
  VendorLogoStyled,
} from './bannerCourses.styled';
import {
  browserService,
  ButtonColor,
  ButtonContainer,
  DeviceInfoContext,
  getRedirectLMSHref,
  Nullable,
  personalPath,
  projectSubDomains,
  RegisterPositions,
  urlService,
  useRegisterPopupContext,
  useTheme,
  useTranslations,
  useUser,
  VendorEnum,
  VendorsLogoContainer,
} from '@lerna-core';
import { BannerContainer } from '../banner';
import { getBannerCourse } from './bannerCourses.helper';
import { useRouter } from 'next/router';

export const BannerCoursesContainer = ({
  handleClickGoToLms,
  handleClickGoToCourses,
}: BannerCoursesContainerProps): Nullable<ReactElement> => {
  const { user, userCoursesCount } = useUser();
  const translations = useTranslations();
  const { handleAction } = useRegisterPopupContext();
  const deviceInfoContext = useContext(DeviceInfoContext);
  const isMobile = browserService.mobileDetectFromContext(deviceInfoContext);
  const router = useRouter();
  const theme = useTheme();
  const myCoursesButtonLabel = isMobile
    ? translations.banner_courses_button_my_education_mobile
    : translations.banner_courses_button_my_education;
  const goToLMSButtonLabel = isMobile
    ? translations.banner_courses_button_go_to_lms_mobile
    : translations.banner_courses_button_go_to_lms;

  const bannerCourse = useMemo(() => getBannerCourse(user!), [user!.courses]);
  const coursesHref = useMemo(
    () =>
      urlService.getFullUrlToSubDomain(
        projectSubDomains.personal,
        personalPath.courses
      ),
    []
  );
  const courseLmsHref = useMemo(
    () =>
      urlService.getFullUrlToSubDomain(
        projectSubDomains.personal,
        getRedirectLMSHref(bannerCourse.uuid)
      ),
    []
  );

  const handleGoToLMS = () => {
    typeof handleClickGoToLms === 'function' && handleClickGoToLms();

    handleAction(
      {
        onSuccess: () => {
          window.open(courseLmsHref, '_blank');
        },
      },
      RegisterPositions.bannerOnCatalog
    );
  };

  const handleGoToCourses = () => {
    typeof handleClickGoToCourses === 'function' && handleClickGoToCourses();

    handleAction(
      {
        onSuccess: () => {
          router.push(coursesHref);
        },
      },
      RegisterPositions.bannerOnCatalog
    );
  };

  return (
    <BannerContainer>
      <CustomerBannerContainerStyled>
        <BannerCoursesWrapStyled>
          <BannerCoursesHeadStyled>
            <CustomerBannerLabelStyled>
              {translations.banner_courses_label}:
            </CustomerBannerLabelStyled>
            {bannerCourse.vendor && (
              <VendorLogoStyled>
                <VendorsLogoContainer
                  fillColor={theme.COLOR.WHITE}
                  vendor={bannerCourse.vendor.slug as VendorEnum}
                  height={20}
                />
              </VendorLogoStyled>
            )}
          </BannerCoursesHeadStyled>
          <BannerCoursesTitleStyled>
            {bannerCourse.title}
          </BannerCoursesTitleStyled>
        </BannerCoursesWrapStyled>
        <BannerCoursesButtonsStyled>
          <ButtonContainer
            color={ButtonColor.white}
            title={goToLMSButtonLabel}
            onClick={handleGoToLMS}
          />
          {userCoursesCount > 1 && (
            <ButtonContainer
              color={ButtonColor.transparent}
              borderColor={theme.COLOR.WHITE_32}
              textColor={theme.COLOR.WHITE}
              onClick={handleGoToCourses}
            >
              <>
                {myCoursesButtonLabel} <sup>{userCoursesCount}</sup>
              </>
            </ButtonContainer>
          )}
        </BannerCoursesButtonsStyled>
      </CustomerBannerContainerStyled>
    </BannerContainer>
  );
};
